import React, { useState, useRef, useEffect } from 'react';

type RenderMode = 'iframe' | 'html-content' | 'proxy';

const Browser: React.FC = () => {
  const [url, setUrl] = useState('https://example.com');
  const [currentUrl, setCurrentUrl] = useState('https://example.com');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string>('');
  const [renderMode, setRenderMode] = useState<RenderMode>('iframe');
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [retryAttempt, setRetryAttempt] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Enhanced proxy services (including working ones from research)
  const CORS_PROXIES = [
    'https://api.allorigins.win/raw?url=',
    'https://thingproxy.freeboard.io/fetch/',
    'https://corsproxy.io/?',
    'https://cors-anywhere.herokuapp.com/',
  ];

  // HTML content fetching proxies (for getting raw HTML)
  const HTML_FETCH_PROXIES = [
    'https://api.allorigins.win/get?url=',
    'https://corsproxy.io/?',
  ];

  const handleNavigate = (mode: RenderMode = 'iframe', proxyIndex = 0) => {
    if (url.trim()) {
      let formattedUrl = url.trim();
      
      if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
        formattedUrl = 'https://' + formattedUrl;
      }
      
      setIsLoading(true);
      setHasError(false);
      setBlockedUrl('');
      setRenderMode(mode);
      setRetryAttempt(proxyIndex);
      
      if (mode === 'html-content') {
        fetchHtmlContent(formattedUrl, proxyIndex);
      } else if (mode === 'proxy') {
        const proxyUrl = CORS_PROXIES[proxyIndex] + encodeURIComponent(formattedUrl);
        setCurrentUrl(proxyUrl);
      } else {
        setCurrentUrl(formattedUrl);
      }
      
      setUrl(formattedUrl);
    }
  };

  const fetchHtmlContent = async (targetUrl: string, proxyIndex = 0) => {
    try {
      setIsLoading(true);
      
      // Try direct fetch first
      if (proxyIndex === 0) {
        try {
          const response = await fetch(targetUrl, { mode: 'no-cors' });
          if (response.ok) {
            const html = await response.text();
            setHtmlContent(html);
            setIsLoading(false);
            return;
          }
        } catch {
          // Fall through to proxy
        }
      }
      
      // Use HTML fetch proxy
      if (proxyIndex < HTML_FETCH_PROXIES.length) {
        const proxyUrl = HTML_FETCH_PROXIES[proxyIndex] + encodeURIComponent(targetUrl);
        const response = await fetch(proxyUrl);
        
        if (response.ok) {
          const data = await response.json();
          const html = data.contents || data.data || '';
          
          // Clean and sanitize HTML
          const cleanHtml = sanitizeHtml(html, targetUrl);
          setHtmlContent(cleanHtml);
          setIsLoading(false);
          return;
        }
      }
      
      // Try next proxy or fail
      if (proxyIndex + 1 < HTML_FETCH_PROXIES.length) {
        fetchHtmlContent(targetUrl, proxyIndex + 1);
      } else {
        throw new Error('All HTML fetch methods failed');
      }
      
    } catch (error) {
      console.error('HTML fetch failed:', error);
      setIsLoading(false);
      setHasError(true);
      setBlockedUrl(targetUrl);
    }
  };

  const sanitizeHtml = (html: string, baseUrl: string): string => {
    try {
      // Basic HTML cleaning and URL fixing
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Convert relative URLs to absolute
      const base = new URL(baseUrl);
      
      // Fix images
      doc.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src');
        if (src && !src.startsWith('http')) {
          img.setAttribute('src', new URL(src, base).href);
        }
      });
      
      // Fix links
      doc.querySelectorAll('a').forEach(link => {
        const href = link.getAttribute('href');
        if (href && !href.startsWith('http') && !href.startsWith('#')) {
          link.setAttribute('href', new URL(href, base).href);
        }
        // Open links in parent window
        link.setAttribute('target', '_parent');
      });
      
      // Remove scripts for security
      doc.querySelectorAll('script').forEach(script => script.remove());
      
      return doc.documentElement.outerHTML;
    } catch {
      return html; // Return original if sanitization fails
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNavigate();
    }
  };

  const handleGoClick = () => {
    handleNavigate();
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    
    // Try HTML content mode if iframe fails
    if (renderMode === 'iframe') {
      console.log('Iframe failed, trying HTML content mode...');
      setTimeout(() => {
        handleNavigate('html-content', 0);
      }, 1000);
      return;
    }
    
    // Try next proxy if in proxy mode
    if (renderMode === 'proxy' && retryAttempt + 1 < CORS_PROXIES.length) {
      console.log(`Proxy ${retryAttempt + 1} failed, trying proxy ${retryAttempt + 2}...`);
      setTimeout(() => {
        handleNavigate('proxy', retryAttempt + 1);
      }, 1000);
      return;
    }
    
    setHasError(true);
    setBlockedUrl(url);
  };

  const openInNewTab = (urlToOpen: string) => {
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  };

  const handleQuickLink = (linkUrl: string) => {
    setUrl(linkUrl);
    handleNavigate('iframe');
  };

  const switchRenderMode = (mode: RenderMode) => {
    if (url) {
      handleNavigate(mode, 0);
    }
  };

  const quickLinks = [
    { name: 'Wikipedia', url: 'https://www.wikipedia.org' },
    { name: 'Archive.org', url: 'https://archive.org' },
    { name: 'Internet Archive', url: 'https://web.archive.org' },
    { name: 'Example.com', url: 'https://example.com' },
  ];

  // Auto-scroll for HTML content mode
  useEffect(() => {
    if (renderMode === 'html-content' && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [htmlContent, renderMode]);

  return (
    <div className="browser">
      {/* Browser Toolbar */}
      <div className="browser-toolbar">
        <div className="browser-nav-buttons">
          <button className="browser-button" title="Back">
            ⬅️
          </button>
          <button className="browser-button" title="Forward">
            ➡️
          </button>
          <button className="browser-button" title="Refresh" onClick={() => handleNavigate(renderMode, 0)}>
            🔄
          </button>
          <button className="browser-button" title="Home" onClick={() => handleQuickLink('https://example.com')}>
            🏠
          </button>
        </div>
        
        <div className="address-bar-container">
          <span className="address-label">Address:</span>
          <input
            type="text"
            className="browser-address-bar"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter website URL..."
          />
          <button className="browser-go-button" onClick={handleGoClick}>
            Go
          </button>
        </div>

        {/* Render Mode Selector */}
        <div className="render-mode-selector">
          <button 
            className={`mode-button ${renderMode === 'iframe' ? 'active' : ''}`}
            onClick={() => switchRenderMode('iframe')}
            title="Iframe Mode (with X-Frame-Options bypass)"
          >
            🖼️
          </button>
          <button 
            className={`mode-button ${renderMode === 'html-content' ? 'active' : ''}`}
            onClick={() => switchRenderMode('html-content')}
            title="HTML Content Mode"
          >
            📄
          </button>
          <button 
            className={`mode-button ${renderMode === 'proxy' ? 'active' : ''}`}
            onClick={() => switchRenderMode('proxy')}
            title="Proxy Mode"
          >
            🔄
          </button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="browser-links">
        <span className="links-label">Quick Links:</span>
        {quickLinks.map((link) => (
          <button
            key={link.name}
            className="quick-link-button"
            onClick={() => handleQuickLink(link.url)}
          >
            {link.name}
          </button>
        ))}
      </div>

      {/* Browser Content */}
      <div className="browser-content">
        {isLoading && (
          <div className="browser-loading">
            <div>
              {renderMode === 'iframe' && 'Loading website...'}
              {renderMode === 'html-content' && `Fetching HTML content via proxy (${retryAttempt + 1}/${HTML_FETCH_PROXIES.length})...`}
              {renderMode === 'proxy' && `Loading via proxy (${retryAttempt + 1}/${CORS_PROXIES.length})...`}
            </div>
            <div className="loading-bar">
              <div className="loading-progress"></div>
            </div>
          </div>
        )}
        
        {hasError ? (
          <div className="browser-error">
            <div className="error-content">
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚫</div>
              <h3>Website Cannot Be Displayed</h3>
              <p>The website "{blockedUrl || url}" cannot be displayed.</p>
              <p><strong>What we tried:</strong></p>
              <ul>
                <li>✅ Direct iframe with X-Frame-Options bypass</li>
                <li>✅ HTML content fetching via {HTML_FETCH_PROXIES.length} proxies</li>
                <li>✅ {CORS_PROXIES.length} different CORS proxy servers</li>
                <li>❌ All methods failed - the site has very strong security</li>
              </ul>
              
              <div className="alternative-options">
                <h4>You can:</h4>
                <button 
                  className="open-new-tab-button"
                  onClick={() => openInNewTab(blockedUrl || url)}
                >
                  🔗 Open {new URL(blockedUrl || url).hostname} in New Tab
                </button>
                
                <p style={{ margin: '20px 0 10px 0' }}><strong>Or try these websites that work:</strong></p>
                <div className="suggested-sites">
                  {quickLinks.map((link) => (
                    <button
                      key={link.name}
                      className="suggested-link"
                      onClick={() => handleQuickLink(link.url)}
                    >
                      {link.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : renderMode === 'html-content' ? (
          <div 
            className="browser-html-content"
            ref={contentRef}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : (
          <>
            <iframe
              ref={iframeRef}
              src={currentUrl}
              className="browser-iframe"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              title="Browser Content"
              is="x-frame-bypass"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
            />
            
            {renderMode === 'proxy' && (
              <div className="proxy-indicator">
                📡 Proxy: {CORS_PROXIES[retryAttempt]?.split('://')[1]?.split('/')[0] || 'Unknown'}
              </div>
            )}
            
            {renderMode === 'iframe' && (
              <div className="mode-indicator">
                🛡️ X-Frame-Bypass Mode
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Browser; 